import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
type Row = {
  id: string;
  department_id: string;
  department_name: string;
  item_name: string;
  quantity: string;
  reason: string | null;
  needed_by: string;
  priority: string;
  status: string;
  requested_by_name: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  supplier_name: string | null;
  expected_arrival_date: string | null;
  notes: string | null;
};
type Department = { id: string; department_name: string };
type Person = { id: string; full_name: string };
const today = () =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(
    new Date(),
  );
export default function PurchaseRequestsPage() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [departments, setDepartments] = useState<Department[]>([]),
    [people, setPeople] = useState<Person[]>([]),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [message, setMessage] = useState<string | null>(null),
    [open, setOpen] = useState(false),
    [editing, setEditing] = useState<Row | null>(null);
  const [form, setForm] = useState({
    departmentId: "",
    item: "",
    quantity: "",
    reason: "",
    neededBy: today(),
    priority: "normal",
  });
  const [manage, setManage] = useState({
    assignedTo: "",
    status: "requested",
    supplier: "",
    arrival: "",
    notes: "",
  });
  const load = useCallback(async () => {
    setLoading(true);
    const [r, d, p] = await Promise.all([
      supabase.rpc("get_purchase_requests"),
      supabase
        .from("staff_departments")
        .select("id,department_name")
        .eq("is_active", true)
        .order("sort_order"),
      supabase.rpc("get_purchase_request_assignees"),
    ]);
    if (r.error || d.error || p.error)
      setMessage("発注依頼を読み込めませんでした。");
    else {
      setRows((r.data || []) as Row[]);
      const allowed =
        profile?.access_level === "super_admin"
          ? d.data || []
          : ((d.data || []) as Department[]).filter((x) =>
              profile?.department_ids.includes(x.id),
            );
      setDepartments(allowed as Department[]);
      setPeople((p.data || []) as Person[]);
    }
    setLoading(false);
  }, [profile]);
  useEffect(() => {
    void load();
  }, [load]);
  const active = useMemo(
    () => rows.filter((r) => !["delivered", "cancelled"].includes(r.status)),
    [rows],
  );
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.rpc("create_purchase_request", {
      p_department_id: form.departmentId,
      p_item_name: form.item,
      p_quantity: form.quantity,
      p_reason: form.reason || null,
      p_needed_by: form.neededBy,
      p_priority: form.priority,
    });
    setSaving(false);
    if (error) setMessage("発注依頼を保存できませんでした。");
    else {
      setOpen(false);
      setForm({
        departmentId: departments[0]?.id || "",
        item: "",
        quantity: "",
        reason: "",
        neededBy: today(),
        priority: "normal",
      });
      await load();
    }
  };
  const edit = (r: Row) => {
    setEditing(r);
    setManage({
      assignedTo: r.assigned_to || "",
      status: r.status,
      supplier: r.supplier_name || "",
      arrival: r.expected_arrival_date || "",
      notes: r.notes || "",
    });
  };
  const saveManage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    const { error } = await supabase.rpc("manage_purchase_request", {
      p_id: editing.id,
      p_assigned_to: manage.assignedTo || null,
      p_status: manage.status,
      p_supplier_name: manage.supplier || null,
      p_expected_arrival: manage.arrival || null,
      p_notes: manage.notes || null,
    });
    setSaving(false);
    if (error) setMessage("手配状況を更新できませんでした。");
    else {
      setEditing(null);
      await load();
    }
  };
  return (
    <div className="p-4 md:p-7 max-w-6xl mx-auto space-y-5">
      <header className="flex justify-between items-end gap-3">
        <div>
          <p className="text-xs font-bold tracking-[.16em] uppercase text-primary-700">
            Procurement
          </p>
          <h1 className="text-2xl font-extrabold mt-1">発注依頼</h1>
          <p className="text-sm text-foreground-600 mt-1">
            必要な物、期限、手配担当、到着予定を全事業で共有します。
          </p>
        </div>
        <button
          onClick={() => {
            setForm({ ...form, departmentId: departments[0]?.id || "" });
            setOpen(true);
          }}
          className="px-4 py-2 rounded-lg bg-primary-800 text-white font-bold"
        >
          ＋ 発注依頼
        </button>
      </header>
      {message && (
        <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">
          {message}
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        {[
          ["未完了", active.length],
          ["期限超過", active.filter((r) => r.needed_by < today()).length],
          ["到着済", rows.filter((r) => r.status === "delivered").length],
        ].map(([l, v]) => (
          <div key={String(l)} className="rounded-xl border bg-white p-4">
            <p className="text-xs text-foreground-500">{l}</p>
            <p className="text-xl font-extrabold mt-1">{v}</p>
          </div>
        ))}
      </div>
      <section className="rounded-xl border bg-white overflow-hidden">
        <div className="divide-y">
          {loading ? (
            <p className="p-10 text-center">読み込み中…</p>
          ) : rows.length === 0 ? (
            <p className="p-10 text-center text-foreground-500">
              発注依頼はありません。
            </p>
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                onClick={() => edit(r)}
                className="w-full p-4 text-left hover:bg-background-50 grid md:grid-cols-[1fr_170px_150px] gap-3"
              >
                <div>
                  <div className="flex gap-2 items-center">
                    <b>{r.item_name}</b>
                    <span className="text-xs rounded-full bg-background-100 px-2 py-0.5">
                      {r.quantity}
                    </span>
                    {r.priority === "urgent" && (
                      <span className="text-xs text-red-700 font-bold">
                        至急
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-500 mt-1">
                    {r.department_name} · 依頼 {r.requested_by_name}
                    {r.reason ? ` · ${r.reason}` : ""}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">必要期限</p>
                  <b
                    className={
                      r.needed_by < today() &&
                      !["delivered", "cancelled"].includes(r.status)
                        ? "text-red-700"
                        : ""
                    }
                  >
                    {r.needed_by}
                  </b>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">
                    {statusLabel(r.status)}
                  </p>
                  <b>{r.assigned_to_name || "担当未定"}</b>
                  {r.expected_arrival_date && (
                    <p className="text-xs">
                      到着予定 {r.expected_arrival_date}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </section>
      {open && (
        <Modal title="発注を依頼" close={() => setOpen(false)}>
          <form onSubmit={create} className="space-y-3">
            <Field label="事業・部署">
              <select
                required
                value={form.departmentId}
                onChange={(e) =>
                  setForm({ ...form, departmentId: e.target.value })
                }
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.department_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="品名">
              <input
                required
                value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value })}
              />
            </Field>
            <Field label="数量・単位">
              <input
                required
                placeholder="例：2ケース"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </Field>
            <Field label="いつまでに必要か">
              <input
                type="date"
                required
                value={form.neededBy}
                onChange={(e) => setForm({ ...form, neededBy: e.target.value })}
              />
            </Field>
            <Field label="優先度">
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <option value="normal">通常</option>
                <option value="urgent">至急</option>
              </select>
            </Field>
            <Field label="用途・理由">
              <textarea
                rows={2}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Field>
            <Save saving={saving} />
          </form>
        </Modal>
      )}
      {editing && (
        <Modal title={editing.item_name} close={() => setEditing(null)}>
          <form onSubmit={saveManage} className="space-y-3">
            <Field label="誰が手配するか">
              <select
                value={manage.assignedTo}
                onChange={(e) =>
                  setManage({ ...manage, assignedTo: e.target.value })
                }
              >
                <option value="">担当未定</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="状況">
              <select
                value={manage.status}
                onChange={(e) =>
                  setManage({ ...manage, status: e.target.value })
                }
              >
                {[
                  "requested",
                  "assigned",
                  "ordered",
                  "delivered",
                  "cancelled",
                ].map((s) => (
                  <option key={s} value={s}>
                    {statusLabel(s)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="発注先">
              <input
                value={manage.supplier}
                onChange={(e) =>
                  setManage({ ...manage, supplier: e.target.value })
                }
              />
            </Field>
            <Field label="到着予定日">
              <input
                type="date"
                value={manage.arrival}
                onChange={(e) =>
                  setManage({ ...manage, arrival: e.target.value })
                }
              />
            </Field>
            <Field label="手配メモ">
              <textarea
                rows={2}
                value={manage.notes}
                onChange={(e) =>
                  setManage({ ...manage, notes: e.target.value })
                }
              />
            </Field>
            <Save saving={saving} />
          </form>
        </Modal>
      )}
    </div>
  );
}
function statusLabel(s: string) {
  return (
    (
      {
        requested: "依頼済み",
        assigned: "担当決定",
        ordered: "発注済み",
        delivered: "到着済み",
        cancelled: "取消",
      } as Record<string, string>
    )[s] || s
  );
}
function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl p-5 shadow-xl">
        <div className="flex justify-between mb-4">
          <h2 className="font-bold text-lg">{title}</h2>
          <button onClick={close}>
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="text-sm block">
      {label}
      <div className="mt-1 [&>*]:w-full [&>*]:border [&>*]:rounded-lg [&>*]:px-3 [&>*]:py-2">
        {children}
      </div>
    </label>
  );
}
function Save({ saving }: { saving: boolean }) {
  return (
    <button
      disabled={saving}
      className="w-full py-2.5 rounded-lg bg-primary-800 text-white font-bold disabled:opacity-50"
    >
      {saving ? "保存中…" : "保存"}
    </button>
  );
}
